import React, { Suspense, useEffect, useRef } from 'react';
import { lazy } from '../utils/lazyWithRetry';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './navigation/Header';
import LegalFooter from './legal/LegalFooter';
import useActivityTracker from '../hooks/useActivityTracker';
import ContactSupportWidget from './support/ReportProblemWidget';

// Lazy-load chatbot — it's heavy (~200KB knowledge base + fuzzy search)
const GlobalChatbot = lazy(() => import('./GlobalChatbot'));

/**
 * Shown inside Layout's Suspense while a lazy page chunk loads during navigation.
 * A slim animated progress bar at the top + empty content area keeps the header
 * visible and avoids replacing the whole page with a heavy loader.
 */
const NavLoader = () => {
  const [width, setWidth] = React.useState(8);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setWidth(w => w >= 88 ? w : w + (88 - w) * 0.11);
    }, 180);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '3px',
          width: `${width}%`,
          background: 'linear-gradient(90deg, #2563EB 0%, #059669 100%)',
          boxShadow: '0 0 10px rgba(37,99,235,0.5)',
          transition: 'width 0.25s ease',
          zIndex: 9999,
          borderRadius: '0 2px 2px 0',
        }}
      />
      <div className="main-content min-h-[60vh]" />
    </>
  );
};

/**
 * Main layout component with static header and animated page content.
 * Uses mode="wait" so the old page fades out before the new one fades in,
 * preventing overlapping/jumping pages.
 */
const Layout = ({ children }) => {
  const location = useLocation();
  const debounceRef = useRef(null);

  // Track page visits and time spent for all users
  useActivityTracker();

  // Lock body scroll when any fixed overlay (modal) is present — prevents
  // background page from scrolling on tablets/iPads when a modal is open.
  // Debounced to avoid thrashing on rapid DOM mutations.
  useEffect(() => {
    const syncLock = () => {
      if (debounceRef.current) return;
      debounceRef.current = requestAnimationFrame(() => {
        debounceRef.current = null;
        const hasOverlay = document.querySelector('.fixed.inset-0');
        document.documentElement.classList.toggle('modal-open', !!hasOverlay);
      });
    };
    const obs = new MutationObserver(syncLock);
    obs.observe(document.body, { childList: true, subtree: true });
    syncLock();
    return () => {
      obs.disconnect();
      if (debounceRef.current) cancelAnimationFrame(debounceRef.current);
      document.documentElement.classList.remove('modal-open');
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Static Header - never unmounts during page transitions */}
      <Header />

      {/* Suspense lives inside Layout so the header stays visible during
          lazy-chunk loads. NavLoader shows only a slim top progress bar
          instead of replacing the entire content area. */}
      <Suspense fallback={<NavLoader />}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.main
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            className="main-content"
          >
            {children}
            <LegalFooter />
          </motion.main>
        </AnimatePresence>
      </Suspense>

      {/* Global AI chatbot — lazy loaded, available on every page */}
      <Suspense fallback={null}>
        <GlobalChatbot />
      </Suspense>

      {/* Report a Problem floating widget */}
      <ContactSupportWidget />
    </div>
  );
};

export default Layout;
